import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  QrCode, Clock, MapPin, Download, Settings, Calendar, Users, 
  ChevronLeft, ChevronRight, LogIn, LogOut, Coffee, Play, AlertCircle, CheckCircle, Printer, Pencil, Trash2, Shield
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { openPrintWindow } from '@/utils/printWindow';

import API from '@/config/api';
// API imported from config;

const PERMISSION_LABELS = {
  view_all_hours: { label: 'View All Hours', description: 'Can view all staff time records' },
  edit_hours: { label: 'Edit Hours', description: 'Can edit and delete time punches' },
  manage_qr: { label: 'Manage QR Codes', description: 'Can generate and manage QR codes' },
  view_reports: { label: 'View Reports', description: 'Can view daily/weekly reports' }
};

const TimeClockAdminPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [generatingQR, setGeneratingQR] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStaffPunches, setSelectedStaffPunches] = useState(null);
  const [editingPunch, setEditingPunch] = useState(null);
  const [editPunchForm, setEditPunchForm] = useState({ time: '', date: '' });
  const [addPunchDialogOpen, setAddPunchDialogOpen] = useState(false);
  const [newPunchForm, setNewPunchForm] = useState({
    staff_id: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    punch_type: 'clock_in'
  });
  const [staffList, setStaffList] = useState([]);
  const [staffPermissions, setStaffPermissions] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay()); // Sunday is day 0
    return sunday.toISOString().split('T')[0];
  });
  
  const [formSettings, setFormSettings] = useState({
    center_latitude: '',
    center_longitude: '',
    radius_meters: 50,
    qr_rotation_hours: 24,
    require_pin: true,
    require_shift_match: true
  });
  
  // Attendance issues state
  const [attendanceIssues, setAttendanceIssues] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  
  // Overtime alerts state
  const [overtimeAlerts, setOvertimeAlerts] = useState([]);
  const [loadingOvertime, setLoadingOvertime] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchStaffList();
  }, []);

  useEffect(() => {
    if (settings?.configured) {
      fetchQRCode();
      fetchQRImage();
      fetchDailyReport();
      fetchWeeklyReport();
      fetchOvertimeAlerts();
    }
  }, [settings, selectedDate, weekStart]);

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/staff`, getAuthHeaders());
      setStaffList(response.data);
      
      // Fetch permissions for each staff
      const permissions = {};
      for (const staff of response.data) {
        try {
          const permResponse = await axios.get(
            `${API}/timeclock/staff/${staff.id}/permissions`,
            getAuthHeaders()
          );
          permissions[staff.id] = permResponse.data.permissions || [];
        } catch {
          permissions[staff.id] = [];
        }
      }
      setStaffPermissions(permissions);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const updateStaffPermissions = async (staffId, newPermissions) => {
    try {
      await axios.put(
        `${API}/timeclock/staff/${staffId}/permissions`,
        { permissions: newPermissions },
        getAuthHeaders()
      );
      setStaffPermissions(prev => ({
        ...prev,
        [staffId]: newPermissions
      }));
      toast.success('Permissions updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const togglePermission = (staffId, permission) => {
    const current = staffPermissions[staffId] || [];
    const newPermissions = current.includes(permission)
      ? current.filter(p => p !== permission)
      : [...current, permission];
    updateStaffPermissions(staffId, newPermissions);
  };

  const fetchAttendanceIssues = async (date) => {
    setLoadingAttendance(true);
    try {
      const [issuesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/timeclock/attendance-check?date=${date}`, getAuthHeaders()),
        axios.get(`${API}/timeclock/daily-attendance-summary?date=${date}`, getAuthHeaders())
      ]);
      setAttendanceIssues(issuesRes.data.issues || []);
      setAttendanceSummary(summaryRes.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const fetchOvertimeAlerts = async () => {
    setLoadingOvertime(true);
    try {
      const response = await axios.get(`${API}/timeclock/overtime-alerts?week_start=${weekStart}`, getAuthHeaders());
      setOvertimeAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch overtime alerts:', error);
    } finally {
      setLoadingOvertime(false);
    }
  };

  const recordAttendanceIssue = async (issue) => {
    try {
      await axios.post(`${API}/timeclock/record-attendance-issue`, issue, getAuthHeaders());
      toast.success('Attendance issue recorded to staff file');
      fetchAttendanceIssues(attendanceDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record issue');
    }
  };

  const acknowledgeIssue = async (issueId) => {
    try {
      await axios.post(`${API}/timeclock/acknowledge-issue/${issueId}`, {}, getAuthHeaders());
      toast.success('Issue acknowledged');
      fetchAttendanceIssues(attendanceDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge issue');
    }
  };

  // Edit punch functions
  const openEditPunch = (punch) => {
    const timestamp = new Date(punch.timestamp);
    setEditingPunch(punch);
    setEditPunchForm({
      time: timestamp.toTimeString().slice(0, 5), // HH:MM format
      date: punch.date
    });
  };

  const handleSaveEditPunch = async () => {
    if (!editingPunch) return;
    
    try {
      await axios.put(`${API}/timeclock/admin/punch/${editingPunch.id}`, {
        time: editPunchForm.time,
        date: editPunchForm.date
      }, getAuthHeaders());
      
      toast.success('Punch time updated');
      setEditingPunch(null);
      
      // Refresh the data
      if (selectedStaffPunches) {
        const response = await axios.get(
          `${API}/timeclock/admin/staff-week/${selectedStaffPunches.staff_id}?week_start=${weekStart}`,
          getAuthHeaders()
        );
        setSelectedStaffPunches(response.data);
      }
      fetchWeeklyReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update punch');
    }
  };

  const handleDeletePunch = async (punchId) => {
    if (!window.confirm('Are you sure you want to delete this punch? This action is logged for audit purposes.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/timeclock/admin/punch/${punchId}`, getAuthHeaders());
      toast.success('Punch deleted');
      
      // Refresh the data
      if (selectedStaffPunches) {
        const response = await axios.get(
          `${API}/timeclock/admin/staff-week/${selectedStaffPunches.staff_id}?week_start=${weekStart}`,
          getAuthHeaders()
        );
        setSelectedStaffPunches(response.data);
      }
      fetchWeeklyReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete punch');
    }
  };

  const handleAddPunch = async () => {
    if (!newPunchForm.staff_id || !newPunchForm.date || !newPunchForm.time) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await axios.post(`${API}/timeclock/admin/add-punch`, newPunchForm, getAuthHeaders());
      toast.success('Punch added successfully');
      setAddPunchDialogOpen(false);
      setNewPunchForm({
        staff_id: '',
        date: new Date().toISOString().split('T')[0],
        time: '',
        punch_type: 'clock_in'
      });
      fetchWeeklyReport();
      fetchDailyReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add punch');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/settings`, getAuthHeaders());
      setSettings(response.data);
      if (response.data.configured) {
        setFormSettings({
          center_latitude: response.data.center_latitude || '',
          center_longitude: response.data.center_longitude || '',
          radius_meters: response.data.radius_meters || 50,
          qr_rotation_hours: response.data.qr_rotation_hours || 24,
          require_pin: response.data.require_pin ?? true,
          require_shift_match: response.data.require_shift_match ?? true
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/current-qr`, getAuthHeaders());
      setQrData(response.data);
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
    }
  };

  const fetchQRImage = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/qr-image`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setQrImageUrl(url);
    } catch (error) {
      console.error('Failed to fetch QR image:', error);
    }
  };

  const fetchDailyReport = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/reports/daily?date=${selectedDate}`, getAuthHeaders());
      setDailyReport(response.data);
    } catch (error) {
      console.error('Failed to fetch daily report:', error);
    }
  };

  const fetchWeeklyReport = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/reports/weekly?week_start=${weekStart}`, getAuthHeaders());
      setWeeklyReport(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly report:', error);
    }
  };

  const generateNewQRCode = async () => {
    setGeneratingQR(true);
    try {
      const response = await axios.post(`${API}/timeclock/generate-qr`, {}, getAuthHeaders());
      toast.success(response.data.message || 'New QR code generated!');
      // Refresh QR data and image
      fetchQRCode();
      fetchQRImage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate QR code');
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!formSettings.center_latitude || !formSettings.center_longitude) {
      toast.error('Please enter GPS coordinates');
      return;
    }

    try {
      await axios.post(`${API}/timeclock/settings`, {
        center_latitude: parseFloat(formSettings.center_latitude),
        center_longitude: parseFloat(formSettings.center_longitude),
        radius_meters: parseInt(formSettings.radius_meters),
        qr_rotation_hours: parseInt(formSettings.qr_rotation_hours),
        require_pin: formSettings.require_pin,
        require_shift_match: formSettings.require_shift_match
      }, getAuthHeaders());
      
      toast.success('Settings saved successfully!');
      setSettingsDialogOpen(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }

    toast.info('Getting your location…');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormSettings({
          ...formSettings,
          center_latitude: position.coords.latitude.toFixed(6),
          center_longitude: position.coords.longitude.toFixed(6)
        });
        toast.success('Location captured! Remember to save.');
      },
      (error) => {
        if (error.code === 1) {
          toast.error('Location permission denied. Please allow location access in your browser settings.');
        } else if (error.code === 2) {
          toast.error('Location unavailable. Try again outdoors or check your connection.');
        } else if (error.code === 3) {
          toast.error('Location request timed out. Please try again.');
        } else {
          toast.error('Failed to get location');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const useCurrentLocationAndSave = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }

    toast.info('Getting your center location…');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const payload = {
            ...formSettings,
            center_latitude: parseFloat(position.coords.latitude.toFixed(6)),
            center_longitude: parseFloat(position.coords.longitude.toFixed(6)),
            radius_meters: parseInt(formSettings.radius_meters || 100),
            qr_rotation_hours: parseInt(formSettings.qr_rotation_hours || 24),
            require_pin: !!formSettings.require_pin,
            require_photo: !!formSettings.require_photo
          };
          await axios.post(`${API}/timeclock/settings`, payload, getAuthHeaders());
          setFormSettings({
            ...formSettings,
            center_latitude: payload.center_latitude.toString(),
            center_longitude: payload.center_longitude.toString()
          });
          toast.success('Center location saved! Time clock is now configured.');
          await fetchSettings();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to save location');
        }
      },
      (error) => {
        if (error.code === 1) {
          toast.error('Location permission denied. Please allow location access in your browser settings.');
        } else if (error.code === 2) {
          toast.error('Location unavailable. Try again outdoors or check your connection.');
        } else if (error.code === 3) {
          toast.error('Location request timed out. Please try again.');
        } else {
          toast.error('Failed to get location');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const downloadQRCode = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/qr-image`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timeclock_qr_${selectedDate}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('QR code downloaded!');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  const printQRCode = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/qr-image`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Time Clock QR Code</title>
          <style>
            body { 
              display: flex; 
              flex-direction: column; 
              align-items: center; 
              justify-content: center; 
              min-height: 100vh; 
              margin: 0;
              font-family: Arial, sans-serif;
            }
            h1 { margin-bottom: 10px; color: #333; }
            p { color: #666; margin: 5px 0; }
            img { max-width: 300px; margin: 20px 0; }
            .footer { margin-top: 20px; font-size: 12px; color: #999; }
          </style>
        </head>
        <body>
          <h1>CareKids WI Time Clock</h1>
          <p>Scan this QR code to punch in/out</p>
          <img src="${url}" alt="Time Clock QR Code" />
          <p><strong>Valid for:</strong> ${qrData?.date || 'Today'}</p>
          <p class="footer">GPS verification required &bull; PIN required</p>
        </body>
        </html>
      `;
      openPrintWindow(html);
    } catch (error) {
      toast.error('Failed to print QR code');
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const changeWeek = (weeks) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + (weeks * 7));
    setWeekStart(date.toISOString().split('T')[0]);
  };

  const printWeeklyTimesheet = () => {
    if (!weeklyReport?.staff?.length) {
      toast.error('No data to print');
      return;
    }

    const weekDates = [];
    const startDate = new Date(weeklyReport.week_start);
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      weekDates.push(d.toISOString().split('T')[0]);
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Weekly Timesheet - ${weeklyReport.week_start} to ${weeklyReport.week_end}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 1000px; margin: 0 auto; }
          h1 { color: #4F46E5; text-align: center; margin-bottom: 5px; }
          h2 { color: #666; text-align: center; font-size: 14px; margin-top: 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: center; }
          th { background: #f5f5f5; font-weight: bold; }
          .staff-name { text-align: left; font-weight: 500; }
          .total { font-weight: bold; color: #4F46E5; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #999; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1>CareKids WI</h1>
        <h2>Weekly Timesheet: ${weeklyReport.week_start} to ${weeklyReport.week_end}</h2>
        
        <table>
          <thead>
            <tr>
              <th>Staff Member</th>
              ${dayNames.map(day => `<th>${day}</th>`).join('')}
              <th>Total Hours</th>
            </tr>
          </thead>
          <tbody>
            ${weeklyReport.staff.map(staff => {
              const hoursMap = {};
              staff.daily_hours.forEach(day => {
                hoursMap[day.date] = day.work_hours;
              });
              return `
                <tr>
                  <td class="staff-name">${staff.staff_name}</td>
                  ${weekDates.map(date => `<td>${hoursMap[date] ? hoursMap[date] + 'h' : '-'}</td>`).join('')}
                  <td class="total">${staff.total_work_hours}h</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <p class="footer">Generated on ${new Date().toLocaleString()}</p>
      </body>
      </html>
    `;

    const printWindow = openPrintWindow(printContent);
    if (!printWindow) {
      toast.error('Please allow pop-ups to print');
    }
  };

  const downloadWeeklyTimesheet = () => {
    if (!weeklyReport?.staff?.length) {
      toast.error('No data to download');
      return;
    }

    const weekDates = [];
    const startDate = new Date(weeklyReport.week_start);
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      weekDates.push(d.toISOString().split('T')[0]);
    }

    // Create CSV content
    const headers = ['Staff Member', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Total Hours', 'Break Hours'];
    const rows = weeklyReport.staff.map(staff => {
      const hoursMap = {};
      staff.daily_hours.forEach(day => {
        hoursMap[day.date] = day.work_hours;
      });
      return [
        staff.staff_name,
        ...weekDates.map(date => hoursMap[date] || 0),
        staff.total_work_hours,
        staff.total_break_hours
      ];
    });

    const csvContent = [
      `Weekly Timesheet: ${weeklyReport.week_start} to ${weeklyReport.week_end}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `timesheet_${weeklyReport.week_start}_${weeklyReport.week_end}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Timesheet downloaded!');
  };

  const openEditDialog = async (staff) => {
    // Fetch detailed punches for this staff member for the week
    console.log('Opening edit dialog for staff:', staff);
    console.log('Using weekStart:', weeklyReport?.week_start || weekStart);
    
    if (!staff.staff_id) {
      toast.error('Staff ID not found');
      console.error('Missing staff_id in:', staff);
      return;
    }
    
    // Use the week from the weekly report if available
    const weekToFetch = weeklyReport?.week_start || weekStart;
    
    try {
      const response = await axios.get(
        `${API}/timeclock/admin/staff-week/${staff.staff_id}?week_start=${weekToFetch}`,
        getAuthHeaders()
      );
      
      console.log('Staff week response:', response.data);
      
      setSelectedStaffPunches({
        staff_id: staff.staff_id,
        staff_name: response.data.staff_name || staff.staff_name,
        punches: response.data.punches || [],
        total_work_hours: response.data.total_work_hours || 0,
        total_break_hours: response.data.total_break_hours || 0
      });
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Failed to load punch details:', error);
      toast.error(error.response?.data?.detail || 'Failed to load punch details');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPunchIcon = (type) => {
    const icons = {
      clock_in: <LogIn className="w-4 h-4 text-green-500" />,
      clock_out: <LogOut className="w-4 h-4 text-red-500" />,
      break_start: <Coffee className="w-4 h-4 text-amber-500" />,
      break_end: <Play className="w-4 h-4 text-blue-500" />
    };
    return icons[type] || <Clock className="w-4 h-4" />;
  };

  const getStatusBadge = (status) => {
    const variants = {
      working: 'default',
      on_break: 'warning',
      clocked_out: 'secondary'
    };
    return <Badge variant={variants[status] || 'outline'}>{status?.replace('_', ' ')}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout title="Time Clock Admin">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Time Clock Admin">
      <div className="space-y-6" data-testid="timeclock-admin-page">
        
        {/* Configuration Warning */}
        {!settings?.configured && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Time Clock Not Configured</p>
                  <p className="text-sm text-amber-600">Tap &quot;Use My Current Location&quot; from your phone or tablet at the center to set it up in one click.</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={useCurrentLocationAndSave}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="one-click-location-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Use My Current Location
                </Button>
                <Button variant="outline" onClick={() => setSettingsDialogOpen(true)} data-testid="configure-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  Manual Setup
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="qr" className="space-y-6">
          <TabsList className="grid grid-cols-2 sm:grid-cols-6 h-auto gap-1 w-full max-w-3xl">
            <TabsTrigger value="qr" data-testid="qr-tab">
              <QrCode className="w-4 h-4 mr-2" />
              QR Code
            </TabsTrigger>
            <TabsTrigger value="daily" data-testid="daily-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Daily
            </TabsTrigger>
            <TabsTrigger value="weekly" data-testid="weekly-tab">
              <Users className="w-4 h-4 mr-2" />
              Weekly
            </TabsTrigger>
            <TabsTrigger value="attendance" data-testid="attendance-tab">
              <AlertCircle className="w-4 h-4 mr-2" />
              Attendance
            </TabsTrigger>
            <TabsTrigger value="permissions" data-testid="permissions-tab">
              <Shield className="w-4 h-4 mr-2" />
              Permissions
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* QR Code Tab */}
          <TabsContent value="qr" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-primary" />
                  Current QR Code
                </CardTitle>
              </CardHeader>
              <CardContent>
                {settings?.configured ? (
                  <div className="text-center space-y-4">
                    <div className="bg-white p-8 rounded-xl border-2 border-dashed inline-block relative">
                      {qrImageUrl ? (
                        <>
                          <img 
                            src={qrImageUrl} 
                            alt="Time Clock QR Code" 
                            className="w-48 h-48"
                            data-testid="qr-code-image"
                          />
                          {qrData?.is_expired && (
                            <div className="absolute inset-0 bg-red-500/80 rounded-lg flex items-center justify-center">
                              <span className="text-white font-bold text-lg">EXPIRED</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-48 h-48 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <Badge variant={qrData?.is_expired ? "destructive" : "default"}>
                          {qrData?.is_expired ? "Expired" : "Active"}
                        </Badge>
                        <Badge variant="outline">
                          Rotation: {qrData?.rotation_hours || formSettings.qr_rotation_hours} hours
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Created: {qrData?.created_at ? new Date(qrData.created_at).toLocaleString() : '-'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Expires: {qrData?.expires_at ? new Date(qrData.expires_at).toLocaleString() : '-'}
                      </p>
                    </div>
                    
                    <div className="flex justify-center gap-3 flex-wrap">
                      <Button 
                        variant="default"
                        onClick={generateNewQRCode} 
                        disabled={generatingQR}
                        data-testid="generate-qr-btn"
                      >
                        {generatingQR ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current mr-2"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <QrCode className="w-4 h-4 mr-2" />
                            Generate New Code
                          </>
                        )}
                      </Button>
                      <Button variant="outline" onClick={downloadQRCode} data-testid="download-qr-btn">
                        <Download className="w-4 h-4 mr-2" />
                        Download PNG
                      </Button>
                      <Button variant="outline" onClick={printQRCode} data-testid="print-qr-btn">
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                      </Button>
                    </div>
                    
                    {qrData?.is_expired && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        This QR code has expired. Please generate a new one for staff to punch in.
                      </div>
                    )}
                    
                    <div className="p-4 bg-muted rounded-xl text-sm text-muted-foreground">
                      <p className="font-medium mb-2">Instructions:</p>
                      <ol className="list-decimal list-inside space-y-1 text-left">
                        <li>Print this QR code and post it at the designated punch area</li>
                        <li>Staff must scan the QR code with their phone</li>
                        <li>GPS location will be verified (must be within {formSettings.radius_meters}m)</li>
                        <li>Staff enters their PIN to confirm</li>
                        <li>QR code rotates every {qrData?.rotation_hours || formSettings.qr_rotation_hours} hours - generate new code as needed</li>
                      </ol>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Configure time clock settings to generate QR codes</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Daily Report Tab */}
          <TabsContent value="daily" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    Daily Attendance
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button 
                      variant="outline" 
                      className="w-full sm:w-auto"
                      onClick={() => setAddPunchDialogOpen(true)}
                      data-testid="add-manual-punch-btn"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Add Manual Punch
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => changeDate(-1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-40"
                      data-testid="daily-date-input"
                    />
                    <Button variant="outline" size="icon" onClick={() => changeDate(1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {dailyReport ? (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="p-4 bg-muted rounded-xl text-center">
                        <p className="text-2xl font-bold">{dailyReport.summary.total_staff_punched}</p>
                        <p className="text-sm text-muted-foreground">Total Punched</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-green-600">{dailyReport.summary.currently_working}</p>
                        <p className="text-sm text-green-700">Working</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-amber-600">{dailyReport.summary.on_break}</p>
                        <p className="text-sm text-amber-700">On Break</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-gray-600">{dailyReport.summary.clocked_out}</p>
                        <p className="text-sm text-gray-700">Clocked Out</p>
                      </div>
                    </div>

                    {/* Staff List */}
                    <div className="space-y-3">
                      {dailyReport.staff.length > 0 ? (
                        dailyReport.staff.map((staff, idx) => (
                          <div key={idx} className="p-4 border rounded-xl">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <p className="font-medium">{staff.staff_name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {getStatusBadge(staff.status)}
                                  {staff.shift_matched && (
                                    <Badge variant="outline" className="text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Shift Matched
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-primary">{staff.total_work_hours}h</p>
                                <p className="text-sm text-muted-foreground">
                                  Break: {staff.total_break_hours}h
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              {staff.punches.map((punch, pIdx) => (
                                <div key={pIdx} className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm">
                                  {getPunchIcon(punch.punch_type)}
                                  <span>{formatTime(punch.timestamp)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>No punches recorded for this date</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Loading report...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weekly Report Tab */}
          <TabsContent value="weekly" className="space-y-4">
            {/* Overtime Alerts */}
            {overtimeAlerts.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-5 h-5" />
                    Overtime Alerts ({overtimeAlerts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {overtimeAlerts.map((alert, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg ${
                          alert.alert_type === 'overtime' 
                            ? 'bg-red-100 border border-red-300' 
                            : 'bg-amber-100 border border-amber-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{alert.staff_name}</span>
                            <Badge 
                              variant={alert.alert_type === 'overtime' ? 'destructive' : 'warning'}
                              className="ml-2"
                            >
                              {alert.total_hours}h total
                            </Badge>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {alert.alert_type === 'overtime' 
                              ? `${alert.overtime_hours}h overtime`
                              : `${alert.hours_until_overtime}h until OT`
                            }
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Weekly Timesheet
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="icon" onClick={() => changeWeek(-1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm px-3">
                      {weeklyReport?.week_start} - {weeklyReport?.week_end}
                    </span>
                    <Button variant="outline" size="icon" onClick={() => changeWeek(1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <div className="border-l pl-2 ml-2 flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={downloadWeeklyTimesheet}
                        disabled={!weeklyReport?.staff?.length}
                        data-testid="download-timesheet-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download CSV
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={printWeeklyTimesheet}
                        disabled={!weeklyReport?.staff?.length}
                        data-testid="print-timesheet-btn"
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {weeklyReport?.staff?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="mobile-cards w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">Staff</th>
                          <th className="text-center p-3">Sun</th>
                          <th className="text-center p-3">Mon</th>
                          <th className="text-center p-3">Tue</th>
                          <th className="text-center p-3">Wed</th>
                          <th className="text-center p-3">Thu</th>
                          <th className="text-center p-3">Fri</th>
                          <th className="text-center p-3">Sat</th>
                          <th className="text-right p-3">Total</th>
                          <th className="text-center p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklyReport.staff.map((staff, idx) => {
                          // Build daily hours map
                          const hoursMap = {};
                          staff.daily_hours.forEach(day => {
                            hoursMap[day.date] = day.work_hours;
                          });
                          
                          // Get dates for the week
                          const weekDates = [];
                          const startDate = new Date(weeklyReport.week_start);
                          for (let i = 0; i < 7; i++) {
                            const d = new Date(startDate);
                            d.setDate(startDate.getDate() + i);
                            weekDates.push(d.toISOString().split('T')[0]);
                          }
                          
                          return (
                            <tr key={idx} className="border-b hover:bg-muted/50">
                              <td className="p-3 font-medium">{staff.staff_name}</td>
                              {weekDates.map((date, dIdx) => (
                                <td key={dIdx} className="text-center p-3">
                                  {hoursMap[date] ? (
                                    <span className="font-mono">{hoursMap[date]}h</span>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </td>
                              ))}
                              <td className="text-right p-3">
                                <span className="font-bold text-primary">{staff.total_work_hours}h</span>
                              </td>
                              <td className="text-center p-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(staff)}
                                  data-testid={`edit-staff-${idx}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No data for this week</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-primary" />
                    Attendance Issues
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="date"
                      value={attendanceDate}
                      onChange={(e) => setAttendanceDate(e.target.value)}
                      className="w-40"
                    />
                    <Button 
                      onClick={() => fetchAttendanceIssues(attendanceDate)} 
                      disabled={loadingAttendance}
                      data-testid="check-attendance-btn"
                    >
                      {loadingAttendance ? 'Checking...' : 'Check Attendance'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary Cards */}
                {attendanceSummary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                      <div className="text-2xl font-bold text-green-700">{attendanceSummary.on_time}</div>
                      <div className="text-sm text-green-600">On Time</div>
                    </div>
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                      <div className="text-2xl font-bold text-amber-700">{attendanceSummary.late}</div>
                      <div className="text-sm text-amber-600">Late Arrivals</div>
                    </div>
                    <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                      <div className="text-2xl font-bold text-red-700">{attendanceSummary.no_shows}</div>
                      <div className="text-sm text-red-600">No Shows</div>
                    </div>
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="text-2xl font-bold text-blue-700">{attendanceSummary.attendance_rate}%</div>
                      <div className="text-sm text-blue-600">Attendance Rate</div>
                    </div>
                  </div>
                )}

                {/* Issues List */}
                {attendanceIssues.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p className="font-medium">No attendance issues found</p>
                    <p className="text-sm">Click "Check Attendance" to analyze today's schedule vs. punches</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      {attendanceIssues.length} issue{attendanceIssues.length !== 1 ? 's' : ''} found
                    </div>
                    {attendanceIssues.map((issue) => (
                      <div 
                        key={issue.id} 
                        className={`p-4 rounded-lg border ${
                          issue.severity === 'high' ? 'border-red-200 bg-red-50' :
                          issue.severity === 'medium' ? 'border-amber-200 bg-amber-50' :
                          'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold">{issue.staff_name}</span>
                              <Badge variant={
                                issue.type === 'no_show' ? 'destructive' :
                                issue.type === 'late_arrival' ? 'warning' :
                                'secondary'
                              }>
                                {issue.type === 'no_show' ? 'No Show' :
                                 issue.type === 'late_arrival' ? 'Late' :
                                 issue.type === 'early_departure' ? 'Left Early' : issue.type}
                              </Badge>
                              <Badge variant="outline" className={
                                issue.severity === 'high' ? 'text-red-700' :
                                issue.severity === 'medium' ? 'text-amber-700' :
                                'text-gray-700'
                              }>
                                {issue.severity}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{issue.description}</p>
                            {issue.minutes_late && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Scheduled: {issue.scheduled_start} | Arrived: {issue.actual_start}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => recordAttendanceIssue(issue)}
                              title="Record to staff file"
                            >
                              Record
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Staff Time Clock Permissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 bg-muted rounded-lg text-sm">
                  <p className="text-muted-foreground">
                    Manage which staff members can view all hours, edit time entries, and access reports.
                    All staff can view their own hours via "My Hours" page.
                  </p>
                </div>
                
                {staffList.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="mobile-cards w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">Staff Member</th>
                          {Object.entries(PERMISSION_LABELS).map(([key, { label }]) => (
                            <th key={key} className="text-center p-3 text-sm">{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {staffList.map((staff, idx) => (
                          <tr key={staff.id} className="border-b hover:bg-muted/50">
                            <td className="p-3">
                              <p className="font-medium">{staff.user_name || staff.name || staff.user_email || staff.email || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">{staff.user_email || staff.email || staff.position || ''}</p>
                            </td>
                            {Object.keys(PERMISSION_LABELS).map(perm => (
                              <td key={perm} className="text-center p-3">
                                <Checkbox
                                  checked={(staffPermissions[staff.id] || []).includes(perm)}
                                  onCheckedChange={() => togglePermission(staff.id, perm)}
                                  data-testid={`perm-${staff.id}-${perm}`}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No staff members found</p>
                  </div>
                )}
                
                <div className="mt-6 space-y-2 text-sm text-muted-foreground">
                  <p className="font-medium">Permission Descriptions:</p>
                  {Object.entries(PERMISSION_LABELS).map(([key, { label, description }]) => (
                    <p key={key}>• <strong>{label}</strong>: {description}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Time Clock Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Center GPS Location
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Latitude</Label>
                        <Input
                          type="number"
                          step="0.000001"
                          value={formSettings.center_latitude}
                          onChange={(e) => setFormSettings({...formSettings, center_latitude: e.target.value})}
                          placeholder="e.g., 43.0389"
                          data-testid="latitude-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Longitude</Label>
                        <Input
                          type="number"
                          step="0.000001"
                          value={formSettings.center_longitude}
                          onChange={(e) => setFormSettings({...formSettings, center_longitude: e.target.value})}
                          placeholder="e.g., -87.9065"
                          data-testid="longitude-input"
                        />
                      </div>
                      <Button variant="outline" onClick={getCurrentLocation} className="w-full">
                        <MapPin className="w-4 h-4 mr-2" />
                        Use Current Location
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>GPS Radius (meters)</Label>
                      <Select 
                        value={String(formSettings.radius_meters)} 
                        onValueChange={(v) => setFormSettings({...formSettings, radius_meters: parseInt(v)})}
                      >
                        <SelectTrigger data-testid="radius-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="50">50m (Strict)</SelectItem>
                          <SelectItem value="100">100m (Moderate)</SelectItem>
                          <SelectItem value="200">200m (Lenient)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Staff must be within this distance to punch
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <QrCode className="w-4 h-4" />
                      Security Settings
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>QR Code Rotation</Label>
                        <Select 
                          value={String(formSettings.qr_rotation_hours)} 
                          onValueChange={(v) => setFormSettings({...formSettings, qr_rotation_hours: parseInt(v)})}
                        >
                          <SelectTrigger data-testid="rotation-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">4 hours</SelectItem>
                            <SelectItem value="8">8 hours (1 shift)</SelectItem>
                            <SelectItem value="12">12 hours</SelectItem>
                            <SelectItem value="24">24 hours (daily)</SelectItem>
                            <SelectItem value="48">48 hours</SelectItem>
                            <SelectItem value="168">168 hours (weekly)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          QR codes expire after this time. You can also generate new codes manually anytime.
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium">Require PIN</p>
                          <p className="text-sm text-muted-foreground">Staff must enter PIN to punch</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formSettings.require_pin}
                          onChange={(e) => setFormSettings({...formSettings, require_pin: e.target.checked})}
                          className="h-5 w-5"
                          data-testid="require-pin-checkbox"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium">Verify Scheduled Shift</p>
                          <p className="text-sm text-muted-foreground">Check if staff has scheduled shift</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formSettings.require_shift_match}
                          onChange={(e) => setFormSettings({...formSettings, require_shift_match: e.target.checked})}
                          className="h-5 w-5"
                          data-testid="require-shift-checkbox"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveSettings} data-testid="save-settings-btn">
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Punches Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5" />
                Edit Time Punches - {selectedStaffPunches?.staff_name}
              </DialogTitle>
            </DialogHeader>
            {selectedStaffPunches && (
              <div className="space-y-4 mt-4">
                {/* Debug info - remove in production */}
                {console.log('Rendering dialog with punches:', selectedStaffPunches?.punches?.length)}
                
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Week: {weeklyReport?.week_start} - {weeklyReport?.week_end}
                  </p>
                  <p className="text-sm font-medium">
                    Total Hours: {selectedStaffPunches.total_work_hours}h (Break: {selectedStaffPunches.total_break_hours}h)
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedStaffPunches.punches?.length || 0} punch records found
                  </p>
                </div>
                
                {selectedStaffPunches.punches?.length > 0 ? (
                  <div className="space-y-3">
                    {/* Group punches by date */}
                    {Object.entries(
                      selectedStaffPunches.punches.reduce((acc, punch) => {
                        if (!acc[punch.date]) acc[punch.date] = [];
                        acc[punch.date].push(punch);
                        return acc;
                      }, {})
                    ).sort(([a], [b]) => a.localeCompare(b)).map(([date, punches]) => (
                      <div key={date} className="border rounded-lg p-3">
                        <p className="font-medium mb-2">{new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
                        <div className="space-y-2">
                          {punches.sort((a, b) => a.timestamp.localeCompare(b.timestamp)).map((punch, pIdx) => (
                            <div key={pIdx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                              <div className="flex items-center gap-3">
                                {getPunchIcon(punch.punch_type)}
                                <span className="capitalize font-medium">{punch.punch_type.replace('_', ' ')}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <Label className="text-sm text-muted-foreground">Time:</Label>
                                  <Input
                                    type="time"
                                    defaultValue={new Date(punch.timestamp).toTimeString().slice(0, 5)}
                                    className="w-32"
                                    data-testid={`edit-time-${punch.id}`}
                                    onBlur={async (e) => {
                                      const newTime = e.target.value;
                                      const oldTime = new Date(punch.timestamp).toTimeString().slice(0, 5);
                                      if (newTime !== oldTime) {
                                        try {
                                          await axios.put(`${API}/timeclock/admin/punch/${punch.id}`, {
                                            time: newTime,
                                            date: punch.date
                                          }, getAuthHeaders());
                                          toast.success('Time updated');
                                          // Refresh data
                                          const response = await axios.get(
                                            `${API}/timeclock/admin/staff-week/${selectedStaffPunches.staff_id}?week_start=${weekStart}`,
                                            getAuthHeaders()
                                          );
                                          setSelectedStaffPunches(prev => ({
                                            ...prev,
                                            punches: response.data.punches,
                                            total_work_hours: response.data.total_work_hours,
                                            total_break_hours: response.data.total_break_hours
                                          }));
                                          fetchWeeklyReport();
                                        } catch (error) {
                                          toast.error('Failed to update time');
                                          e.target.value = oldTime;
                                        }
                                      }
                                    }}
                                  />
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {punch.manual_entry ? 'Manual' : `${punch.distance_from_center}m`}
                                </Badge>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleDeletePunch(punch.id)}
                                  title="Delete punch"
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No punches recorded for this week</p>
                  </div>
                )}
                
                <div className="pt-4 border-t text-sm text-muted-foreground">
                  <p>⚠️ All edits are logged for audit purposes with timestamp and admin ID.</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Manual Punch Dialog */}
        <Dialog open={addPunchDialogOpen} onOpenChange={setAddPunchDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Add Manual Punch
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Staff Member</Label>
                <Select
                  value={newPunchForm.staff_id}
                  onValueChange={(v) => setNewPunchForm({...newPunchForm, staff_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map(staff => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.user_name || staff.user_email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={newPunchForm.date}
                    onChange={(e) => setNewPunchForm({...newPunchForm, date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={newPunchForm.time}
                    onChange={(e) => setNewPunchForm({...newPunchForm, time: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Punch Type</Label>
                <Select
                  value={newPunchForm.punch_type}
                  onValueChange={(v) => setNewPunchForm({...newPunchForm, punch_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clock_in">Clock In</SelectItem>
                    <SelectItem value="clock_out">Clock Out</SelectItem>
                    <SelectItem value="break_start">Break Start</SelectItem>
                    <SelectItem value="break_end">Break End</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="pt-4 flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setAddPunchDialogOpen(false)}>
                  Cancel
                </Button>
                <Button className="flex-1" onClick={handleAddPunch}>
                  Add Punch
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Manual punches are logged for audit purposes.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TimeClockAdminPage;
